import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BookingManagement from '../../components/BookingManagement';
import ServicesManagement from '../../components/ServicesManagement';
import OrganizeServices from '../../components/OrganizeServices';
import PromotionsManagement from '../../components/PromotionsManagement';
import TransactionManagement from '../../components/TransactionManagement';
import NewsAndCommitments from '../../components/NewsAndCommitments';
import '../../css/adminDashboard.css';

function AdminDashboard() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth) {
      try {
        const parsedAuth = JSON.parse(auth);
        setCurrentUser(parsedAuth);
        // Set the default active section based on user role
        if (parsedAuth.role === 'booker') {
          setActiveSection('bookings');
        } else if (parsedAuth.role === 'cashier') {
          setActiveSection('transactions');
        } else {
          setActiveSection('dashboard');
        }
      } catch (error) {
        console.error('Error parsing auth data:', error);
      }
    }
  }, []);

  // Role-based access control for sections
  const hasAccessToSection = (sectionId) => {
    if (!currentUser) return false;

    const sectionRoles = {
      'dashboard': ['super-admin', 'admin'],
      'bookings': ['super-admin', 'admin', 'booker'],
      'services': ['super-admin', 'admin'],
      'organize': ['super-admin', 'admin'],
      'promotions': ['super-admin', 'admin'],
      'payments': ['super-admin', 'admin', 'cashier'],
      'transactions': ['super-admin', 'admin', 'cashier'],
      'news-commitments': ['super-admin', 'admin']
    };

    const allowedRoles = sectionRoles[sectionId] || [];
    return allowedRoles.includes(currentUser.role);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin/login');
  };

  const renderSectionContent = () => {
    // Check if user has access to the selected section
    if (!hasAccessToSection(activeSection)) {
      return (
        <div className="section-content">
          <div className="access-denied">
            <i className="fas fa-lock"></i>
            <h2>Access Denied</h2>
            <p>You don't have permission to access this section.</p>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="section-content">
            <h2>Dashboard</h2>
            <p>System overview and analytics</p>
            <div className="content-placeholder">
              <div className="placeholder-card">
                <h3>System Overview</h3>
                <p>Monitor system performance and key metrics</p>
              </div>
              <div className="placeholder-card">
                <h3>Quick Stats</h3>
                <p>Recent bookings, services, and revenue overview</p>
              </div>
            </div>
          </div>
        );
      case 'bookings':
        return (
          <div className="section-content">
            <BookingManagement />
          </div>
        );
      case 'services':
        return (
          <div className="section-content">
            <ServicesManagement />
          </div>
        );
      case 'organize':
        return (
          <div className="section-content">
            <OrganizeServices />
          </div>
        );
      case 'promotions':
        return (
          <div className="section-content">
            <PromotionsManagement />
          </div>
        );
      case 'payments':
        return (
          <div className="section-content">
            <h2>Payment Management</h2>
            <p>Payment processing and financial operations</p>
            <div className="content-placeholder">
              <div className="placeholder-card">
                <h3>Payment Processing</h3>
                <p>Handle payments, refunds, and transactions</p>
              </div>
              <div className="placeholder-card">
                <h3>Financial Reports</h3>
                <p>Revenue analytics and financial insights</p>
              </div>
            </div>
          </div>
        );
      case 'transactions':
        return (
          <div className="section-content">
            <TransactionManagement />
          </div>
        );
      case 'news-commitments':
        return (
          <div className="section-content">
            <NewsAndCommitments />
          </div>
        );
      default:
        return (
          <div className="section-content">
            <div className="no-access">
              <h2>Section Not Found</h2>
              <p>Please select a valid section from the sidebar.</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="admin-dashboard">
      <AdminSidebar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection} 
      />
      <div className="dashboard-content">
        <div className="content-header">
          <h1>Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {currentUser?.username || 'Admin'}</span>
            <button className="logout-btn" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          </div>
        </div>
        <main className="main-content">
          {renderSectionContent()}
        </main>
      </div>
    </div>
  );
}

export default AdminDashboard;